import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function GuidedSession({ title, minutes, steps }) {
  const total = minutes * 60;
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!running) {
      clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setElapsed((e) => {
        if (e + 1 >= total) {
          setRunning(false);
          return total;
        }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, total]);

  const complete = elapsed >= total;
  // current guidance line = the last step whose `at` <= elapsed
  let current = steps[0];
  for (const s of steps) if (elapsed >= s.at) current = s;

  const remaining = total - elapsed;
  const mm = Math.floor(remaining / 60);
  const ss = (remaining % 60).toString().padStart(2, "0");
  const progress = elapsed / total;

  const R = 92;
  const C = 2 * Math.PI * R;

  return (
    <div data-testid="guided-session" className="flex flex-col items-center">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#F4F1EC]/50 font-body">{title}</div>

      <div className="relative my-8 w-[220px] h-[220px] flex items-center justify-center">
        <svg className="absolute inset-0 -rotate-90" viewBox="0 0 220 220">
          <circle cx="110" cy="110" r={R} fill="none" stroke="rgba(244,241,236,0.12)" strokeWidth="2" />
          <motion.circle
            cx="110"
            cy="110"
            r={R}
            fill="none"
            stroke="url(#wynn-grad)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={C}
            initial={{ strokeDashoffset: C }}
            animate={{ strokeDashoffset: C * (1 - progress) }}
            transition={{ duration: 1, ease: "linear" }}
          />
          <defs>
            <linearGradient id="wynn-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFB7C6" />
              <stop offset="50%" stopColor="#E6BFFF" />
              <stop offset="100%" stopColor="#A8E6FF" />
            </linearGradient>
          </defs>
        </svg>
        <span className="font-body text-[13px] tabular-nums tracking-widest text-[#F4F1EC]/70">
          {complete ? "Complete" : `${mm}:${ss}`}
        </span>
      </div>

      <div className="min-h-[88px] max-w-md text-center px-4">
        <AnimatePresence mode="wait">
          <motion.p
            key={complete ? "done" : current.text}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.7 }}
            className="font-display text-[24px] md:text-[26px] italic font-light leading-[1.4] text-[#F4F1EC]"
          >
            {complete ? "Rest here a moment. Then return to your work." : current.text}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="mt-8 flex gap-3">
        {!running ? (
          <button
            data-testid="guided-start"
            onClick={() => {
              if (complete) setElapsed(0);
              setRunning(true);
            }}
            className="h-11 px-7 font-body text-[11px] uppercase tracking-[0.24em] bg-[#F4F1EC] text-[#070608] hover:bg-white transition-colors"
          >
            {complete ? "Again" : elapsed > 0 ? "Resume" : "Begin"}
          </button>
        ) : (
          <button
            data-testid="guided-pause"
            onClick={() => setRunning(false)}
            className="h-11 px-7 font-body text-[11px] uppercase tracking-[0.24em] border border-[#F4F1EC]/40 text-[#F4F1EC] hover:border-[#FFB7C6] hover:text-[#FFB7C6] transition-colors"
          >
            Pause
          </button>
        )}
        {(elapsed > 0 || complete) && !running && (
          <button
            data-testid="guided-reset"
            onClick={() => {
              setRunning(false);
              setElapsed(0);
            }}
            className="h-11 px-6 font-body text-[11px] uppercase tracking-[0.24em] text-[#F4F1EC]/55 hover:text-[#F4F1EC] transition-colors"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
}
